import { Card, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import type { RecordingMessageDocument } from '@hubertai/screening-question-types';
import { EQuestionType } from '@hubertai/screening-question-types';
import AnswerEvaluation from '../AnswerEvaluation';
import type { TChatCollection } from 'types/scores.types';
import { useGetRecordingSignedUrlQuery, useRetryRecordingTranscriptionMutation } from 'rtk/query/apis/scores';

interface Props {
  collection: TChatCollection;
  answer: RecordingMessageDocument;
}

/**
 * Manual retry for a failed/missing transcription (AddPipe/ElevenLabs occasionally fail the
 * inline attempt on the webhook). Relays to chat-backend's backfill endpoint via mgr-backend.
 */
const RetryTranscription = ({ answerId }: { answerId: string }) => {
  const [retry, { data, isLoading, isError }] = useRetryRecordingTranscriptionMutation();

  if (data?.status === 'ready') {
    return (
      <Text as="div" size="1" color="green">
        Transcript generated — reload the scoring view to see it.
      </Text>
    );
  }

  return (
    <div className={clsx('flex flex-row items-center gap-2')}>
      <button
        type="button"
        className="text-primary self-start text-xs underline disabled:opacity-50"
        onClick={() => retry(answerId)}
        disabled={isLoading}
      >
        {isLoading ? 'Transcribing…' : 'Retry transcription'}
      </button>
      {(isError || (data && data.status !== 'ready')) && (
        <Text as="div" size="1" color="red">
          Transcription failed{data?.status ? ` (${data.status})` : ''}.
        </Text>
      )}
    </div>
  );
};

/**
 * Plays a candidate's audio/video recording answer in the scoring view. The recording
 * lives in a private GCS bucket (chat project), so we fetch a short-lived signed URL from
 * the manager backend (which delegates signing to chat-backend) only once the recording is
 * 'ready'. Audio-only answers use <audio>, otherwise <video>.
 */
const CandidateRecordingAnswer = ({ collection, answer }: Props) => {
  const media = answer.recording_media;
  const isReady = media?.status === 'ready';
  // Element is driven by the question type (source of truth); the answer's audio_only flag
  // is only a fallback. An audio answer must use <audio> — a <video> with an audio-only
  // source renders an empty box.
  const isAudio = collection.question?.type === EQuestionType.Audio || media?.audio_only === true;

  const { data, isFetching, isError } = useGetRecordingSignedUrlQuery(answer._id, { skip: !isReady });

  const renderBody = () => {
    if (answer.declined) {
      return (
        <Text as="div" size="2" color="gray">
          Candidate opted out of this question.
        </Text>
      );
    }
    if (!media) {
      return (
        <Text as="div" size="2" color="gray">
          No recording.
        </Text>
      );
    }
    if (media.status === 'processing') {
      return (
        <Text as="div" size="2" color="gray">
          Recording is still processing…
        </Text>
      );
    }
    if (media.status === 'failed') {
      return (
        <Text as="div" size="2" color="red">
          Recording failed to process.
        </Text>
      );
    }
    if (isFetching) {
      return (
        <Text as="div" size="2" color="gray">
          Loading recording…
        </Text>
      );
    }
    if (isError || !data?.url) {
      return (
        <Text as="div" size="2" color="red">
          Couldn’t load the recording.
        </Text>
      );
    }
    return isAudio ? (
      <audio controls preload="metadata" src={data.url} className={clsx('w-full', 'mb-2')} />
    ) : (
      <video controls preload="metadata" src={data.url} className={clsx('max-h-80 w-full rounded', 'mb-2')} />
    );
  };

  // Speech-to-text transcript (ElevenLabs Scribe, produced by chat-backend on the AddPipe
  // webhook). Rendered independently of the player so a signed-URL hiccup doesn't hide it.
  const renderTranscript = () => {
    if (answer.declined || !isReady) return null;
    const transcript = media?.transcript;
    // Failed or never-attempted transcription on a ready recording → offer a manual retry.
    if (!transcript || transcript.status === 'failed') {
      return (
        <div className={clsx('flex flex-col gap-1', 'border-t border-gray-200 pt-2')}>
          {transcript?.status === 'failed' && (
            <Text as="div" size="1" color="gray">
              Transcript unavailable.
            </Text>
          )}
          <RetryTranscription answerId={String(answer._id)} />
        </div>
      );
    }
    return (
      <div className={clsx('flex flex-col gap-1', 'border-t border-gray-200 pt-2')}>
        <Text as="div" size="1" weight="medium" color="gray">
          Transcript
        </Text>
        <Text as="div" size="2" className="whitespace-pre-wrap">
          {transcript.text?.trim() ? transcript.text : <RetryTranscription answerId={String(answer._id)} />}
        </Text>
      </div>
    );
  };

  return (
    <div className={clsx('flex flex-row justify-end gap-2')}>
      <Card className={clsx('max-w-[80%]', 'flex flex-col justify-between gap-2', 'p-2')}>
        {/* AddPipe stream name — the key linking this answer to bucket objects, media links and
            removal-ledger receipts. select-all so admins can copy it for comparison. */}
        {media?.stream_name && (
          <HubertBadge variant="outline" className={clsx('select-all', 'self-start', 'font-mono text-xs', 'my-2')}>
            {media.stream_name}
          </HubertBadge>
        )}
        {renderBody()}
        {renderTranscript()}
      </Card>
      <AnswerEvaluation />
    </div>
  );
};

export default CandidateRecordingAnswer;
